import { Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FieldArrayType, FormlyFieldConfig } from '@ngx-formly/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { map, takeUntil } from 'rxjs';
import {
  QuestionnaireService,
  REPEAT_GROUP_SUFFIX,
} from 'src/app/core/services';
import { Modal } from 'src/app/models/enums';
import { RepeatFormComponent } from '../..';
import { summaryCardConfigs } from './summary-card.config';

@Component({
  selector: 'app-summary-card',
  templateUrl: './summary-card.component.html',
})
export class SummaryCardComponent extends FieldArrayType {
  requiredFieldLabel$ = this.route.data.pipe(
    map((data) =>
      data['allowIncompletePage']
        ? 'This answer is missing'
        : 'This field is required',
    ),
  );
  showError$ = this.questionnaireService.showErrors$;

  get cardConfig() {
    if (summaryCardConfigs.hasOwnProperty(this.fieldKey)) {
      return summaryCardConfigs[this.fieldKey];
    }

    return null;
  }

  get fieldArray() {
    return this.field.fieldArray as FormlyFieldConfig;
  }

  get fieldKey() {
    return (this.field.key as string).replace(REPEAT_GROUP_SUFFIX, '');
  }

  get labels() {
    return (
      this.cardConfig?.labels || {
        add_first: 'Add one or more entries',
        add_more: 'Add another entry',
      }
    );
  }

  constructor(
    private modalService: BsModalService,
    private questionnaireService: QuestionnaireService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  addGroup(): void {
    // Certain CE fields require numeric counter of repeater groups
    // to be updated in real time for usage property to work correctly.
    // Adding a "band-aid" here for conditional fields to work.
    if (
      this.field.parent?.model &&
      !this.field.parent.model.hasOwnProperty(this.fieldKey)
    ) {
      this.field.parent.model[this.fieldKey] = 1;
    }

    this.add();
  }

  getFieldLabel(name: string) {
    return this.fieldArray.fieldGroup?.find(({ key }) => key === name)?.props
      ?.label;
  }

  getFields() {
    return (
      this.fieldArray.fieldGroup
        ?.map(({ key }) => key as string)
        .reduce<[string[], string[]]>(
          (columns, field, index) => {
            columns[index % 2].push(field);
            return columns;
          },
          [[], []],
        ) || [[], []]
    );
  }

  hasField(name: string) {
    return (
      !name ||
      this.fieldArray?.fieldGroup?.some(({ key }) => key === name.toLowerCase())
    );
  }

  isRowValid(i: number) {
    if (Array.isArray(this.field.fieldGroup)) {
      const row = this.field.fieldGroup[i];
      return row.formControl?.valid;
    }

    return true;
  }

  openRepeatForm(index?: number): void {
    const fields =
      index !== undefined && this.field.fieldGroup
        ? this.field.fieldGroup[index].fieldGroup
        : this.fieldArray.fieldGroup;

    const model =
      index !== undefined
        ? { ...this.formControl.at(index).getRawValue() }
        : {};

    const modalRef = this.modalService.show(RepeatFormComponent, {
      class: 'modal-md modal-dialog-centered',
      id: Modal.RepeaterForm,
      initialState: {
        fields,
        form: new FormGroup({}),
        model,
        operation: index === undefined ? 'add' : 'update',
        repeatName: this.cardConfig?.formLabel,
      },
    });

    modalRef.content?.onSave$
      .pipe(takeUntil(modalRef.content.destroyed$))
      .subscribe((value) => {
        this.formControl.markAsDirty();
        if (index !== undefined) {
          this.remove(index);
          this.add(index, value);
          // We need to notify formly about model changes
          // hence resetModel call below.
          // if (this.options.resetModel) {
          //   this.options.resetModel(this.formControl.root.getRawValue());
          // }
        } else {
          this.add(undefined, value);
        }
      });
  }

  removeGroup(index: number): void {
    this.formControl.markAsTouched();
    if (this.formControl.length === 1) {
      Object.keys(this.formControl.at(0).getRawValue()).forEach((key) => {
        this.formControl.at(0).get(key)?.setValue('', { emitEvent: false });
      });
      this.formControl.markAsDirty();
      this.formControl.updateValueAndValidity();
    }
    this.remove(index, { markAsDirty: true });
    this.formControl.updateValueAndValidity();
  }
}
